import type { InternalBundlerCapabilitiesOptions, ResolveMapEntry } from '@ms-cloudpack/common-types';
import { ensureGeneratedSettingsForPackage, readGeneratedConfig, writeGeneratedConfig } from '@ms-cloudpack/config';
import { parseRequestInfo } from '@ms-cloudpack/import-map';
import {
  addExportsMapEntry,
  findResolveMapEntry,
  flattenExportsMap,
  getExportsMap,
} from '@ms-cloudpack/package-utilities';
import { makeUrl, normalizeRelativePath } from '@ms-cloudpack/path-string-parsing';
import type { PartialContext } from '../types/Context.js';
import type { OverrideOptions } from '../types/OverrideOptions.js';

type OverrideReturn = {
  /** Whether the override was successfully added */
  addedOverride: boolean;
  /** The entry (if any) in the resolve map the override was added to */
  entry: ResolveMapEntry | undefined;
};

/**
 * Helper so that when addOverride is called multiple times in quick succession, we only process one at a time.
 * This is to avoid that multiple calls of addOverride first read the same version of the generated config,
 * then write the same version of the config with its corresponding update,
 * which would result in the last call overwriting the changes of the previous calls.
 */
let overrideInProcess: Promise<OverrideReturn> = Promise.resolve({
  addedOverride: false,
  entry: undefined,
});

/**
 * The addOverride method is triggered by a user action in the overlay, when we detect new usage of an import path
 * that isn't recognized. When the user triggers this override, we write it to the cloudpack generated config.
 */
export async function addOverride(
  options: Omit<OverrideOptions, 'initiatedBy'>,
  context: PartialContext<'packages', 'config' | 'resolveMap'>,
): Promise<OverrideReturn> {
  overrideInProcess = overrideInProcess.then(async () => {
    const { packageName, importPath, version, issuerUrl } = options;
    const { session, packages } = context;
    const { appPath } = session.config;
    let addedOverride = false;
    let deletedNullExport = false;
    let requiredBy = undefined;

    if (importPath.endsWith('/')) {
      console.error(`The import path "${importPath}" is invalid. Please remove the trailing slash and try again.`);
      return { addedOverride, entry: undefined };
    }

    if (issuerUrl) {
      const { packageName: issuerName, version: issuerVersion } = parseRequestInfo(makeUrl(issuerUrl).pathname);
      requiredBy = { name: issuerName, version: issuerVersion };
    }

    const entry = findResolveMapEntry({
      packageName,
      resolveMap: session.resolveMap,
      definition: requiredBy,
      version,
    });

    if (!entry) {
      console.error(`Could not find entry of package "${packageName}" in resolve map`);
      return { addedOverride, entry };
    }

    const packagePath = entry.path;

    // Get current exports. We want to add an additional entry to what's there - not replace the default exports,
    // so we need the full map for the package.
    const exports = await getExportsMap({ packagePath }, { packages, config: session.config });
    const flattenedExports = flattenExportsMap(exports);
    const normalizedImportPath = normalizeRelativePath(importPath);

    // Return false if the import path already exists in the exports map.
    if (flattenedExports[normalizedImportPath]) {
      console.debug(`The import path "${importPath}" already exists in the exports map for "${packageName}".`);
      return { addedOverride, entry };
    }

    // Parse the generated config if it exists.
    const generatedConfig = await readGeneratedConfig(appPath);

    // Ensure packageSettings exists.
    const generatedPackageSettings = await ensureGeneratedSettingsForPackage(
      { packagePath },
      { config: { generated: generatedConfig }, packages },
    );

    if (generatedPackageSettings?.exports && generatedPackageSettings.exports[normalizedImportPath] === null) {
      delete generatedPackageSettings.exports[normalizedImportPath];
      deletedNullExport = true;
      addedOverride = true;
    } else {
      const generatedExports = (generatedPackageSettings.exports ??= {});

      const bundlerCapabilities = ((await packages.get(packagePath)).cloudpack?.bundlerCapabilities ||
        {}) as InternalBundlerCapabilitiesOptions;
      const resolveWebExtensions =
        // eslint-disable-next-line etc/no-deprecated
        bundlerCapabilities?.['resolve-web-extensions'] ?? !!session.config.features?.resolveWebExtensions;

      // Add the single new exports entry to the exports map.
      addedOverride = await addExportsMapEntry(
        {
          exports: generatedExports,
          packagePath,
          importPath,
          resolveWebExtensions,
        },
        { packages },
      );

      if (!addedOverride) {
        console.warn(
          `Unable to add override for package "${packageName}" to "cloudpack.generated.json".` +
            ` The file "${normalizedImportPath}" could not be found.` +
            ` Please check the import path and try again.`,
        );
        return { addedOverride, entry };
      }
    }

    // Write the updated generated config
    session.config.generated = generatedConfig;
    const result = await writeGeneratedConfig(generatedConfig, appPath);
    const desc = `"${packageName}" (path "${normalizedImportPath}")`;
    if (result !== 'no-op') {
      if (deletedNullExport) {
        console.log(`Removed generated null export for ${desc} from "cloudpack.generated.json".`);
        if (result === 'deleted') {
          console.log(`Deleted "cloudpack.generated.json" because it's now empty.`);
        }
      } else if (result === 'written') {
        console.log(`Added override for ${desc} to "cloudpack.generated.json".`);
      }
    }

    // Apply the generated exports again to ensure the exports are up to date.
    await packages.get(packagePath, { refresh: true });

    return { addedOverride, entry };
  });

  return overrideInProcess;
}
